(ns frontend.view.settings2.surveys.survey-type-tab
  (:require
   [clojure.set :as set]
   [clojure.string :as string]
   [common.localization :as loc]
   [common.schema.entities.reminder-rule.options :as reminder-rule-options]
   [common.schema.entities.rotation-period.options :as rotation-period-options]
   [common.schema.entities.survey-cycle.options :as survey-cycle-options]
   [frontend.logging :as logging]
   [frontend.state.current-session :as session]
   [frontend.updates.entity-scope :as entity-scope]
   [frontend.view.entity :as entity]
   [frontend.ux-overhaul.table.event :as table-events]
   [frontend.ux-overhaul.table.helpers :as table-helpers]
   [frontend.ux-overhaul.table.view :as table]
   [frontend.view.modals.delete-confirmation.check-usage-view :as delete-confirmation-check-usage]
   [frontend.view.modals.entity-editor.view :as entity-editor]
   [frontend.view.modals.events :as modals]
   [frontend.view.modals.survey-cycle-actions.view :as survey-cycle-actions-modal]
   [frontend.view.modals.survey-template-email-editor.view :as survey-template-email-editor-modal]
   [frontend.view.modals.survey-template-thank-you-editor.view :as survey-template-thank-you-editor-modal]
   [frontend.permissions.survey-mass-sending :as mass-sending]
   [frontend.view.settings2.surveys.events :as events]
   [frontend.view.settings2.surveys.sub :as sub]
   [re-frame.core :as rf]
   [reagent.core :as r]
   [ui.components.button.view :as button]
   [ui.components.read-only.view :as read-only]))

(defn create-child-entity!
  [entity-type containing-entity-id]
  (entity/open-creator!
   entity-type
   {:prevent-open-entity-after-create? true
    :on-ok!                            (fn [{:keys [close-cb!]}]
                                        (close-cb!))
    :start-value                       {:common/containing-entity containing-entity-id}}))

(defn refresh
  [state]
  (let [grid-ref (get-in @state [:grid :ref])
        grid-api (aget grid-ref "api")
        ;redraw-rows-fn (aget grid-api "redrawRows")
        refresh-cells-fn (aget grid-api "refreshCells")]

    (try
      (.call refresh-cells-fn grid-api (clj->js {:force true}))
      (catch js/Object err
        (js/console.log "Failed to refresh " err)))))

(defn open-entity-editor!
  "Opens the shared entity editor on a child of a survey type.

   `containing-entity-id` is the survey type the child belongs to. It is passed
   as context, not as something the editor may change: a field whose options are
   scoped to the parent - the survey cycle's rotation period, for one - cannot
   work out that parent on its own. A saved child does not carry it (the link
   lives on the survey type as a card-many ref), and a client session map has no
   reverse refs to walk back with. This view does know it, so it says so."
  [{:keys [entity-id containing-entity-id]}]
  (modals/open-modal! entity-editor/modal-key
                      {:entity-id            entity-id
                       :containing-entity-id containing-entity-id}))

(defn resolve-entity
  [entity-or-id]
  (if (:db/id entity-or-id)
    entity-or-id
    (session/get-entity entity-or-id)))

(defn localized-option-label
  [options kw]
  (or (some->> options
               (filter (fn [opt]
                         (= (:key opt) kw)))
               first
               :label)
      (some-> kw name)))

(defn format-cycle-date
  [timestamp]
  (when timestamp
    (.toLocaleDateString (js/Date. timestamp))))

(defn detail-section-title
  [title entity-type containing-entity-id]
  [:div
   {:class "flex items-center justify-between gap-3"}
   [:h4
    {:class "text-sm font-semibold text-base-light-text"}
    title]
   (when (or containing-entity-id
             (= entity-type :entity.type/survey-template))
     [button/square-secondary-open
      {:tooltip  (loc/tr :create)
       :on-click #(if (= entity-type :entity.type/survey-template)
                    (entity/open-creator!
                     :entity.type/survey-template
                     {:prevent-open-entity-after-create? true
                      :on-ok!                            (fn [{:keys [close-cb!]}]
                                                          (close-cb!))
                      :start-value                       {:survey-template/survey-type containing-entity-id}})
                    (create-child-entity! entity-type containing-entity-id))}
      [:i.fas.fa-plus]])])

(defn detail-table
  [rows]
  [:div
   {:class "border border-solid border-base-light-border-paler rounded-lg overflow-hidden"}
   [:table
    {:class "min-w-full divide-y divide-solid divide-base-light-border-paler"}
    (into
     [:tbody
      {:class "divide-y divide-solid divide-base-light-border-paler"}]
     rows)]])

(defn template-detail-row
  [{:keys [template survey-type-id]}]
  [:tr
   {:key   (str "survey-template-detail-" (:db/id template))
    :class "*:px-3 *:py-2 *:text-sm"}
   [:td
    {:class "w-full"}
    [:div {:class "font-medium"} (:survey-template/label template)]
    [:div {:class "text-xs text-base-light-text-lighter"}
     (read-only/multi-value-text {:values    (:survey-template/languages template)
                                  :data-type :data.type/language})]]
   [:td
    {:class "whitespace-nowrap"}
    [:div {:class "flex items-center gap-2 justify-end"}
     ;; Five icons, four of which had nothing to say for themselves. A cog and
     ;; a pen are at least conventional; an envelope and a smiling face for
     ;; "edit the invitation email" and "edit the thank-you page" are not
     ;; guessable from the glyph.
     [button/square-secondary-open
      {:tooltip    (loc/tr :settings.surveys/edit-template-content)
       :aria-label (loc/tr :settings.surveys/edit-template-content)
       :on-click   #(rf/dispatch [::events/start-edit-template (:db/id template)])}
      [:i.fal.fa-cog]]
     [button/square-secondary-open
      {:tooltip    (loc/tr :settings.surveys/edit-template-settings)
       :aria-label (loc/tr :settings.surveys/edit-template-settings)
       :on-click   #(open-entity-editor! {:entity-id            (:db/id template)
                                          :containing-entity-id survey-type-id})}
      [:i.fas.fa-pen]]
     [button/square-secondary-open
      {:tooltip    (loc/tr :settings.surveys/edit-invitation-email)
       :aria-label (loc/tr :settings.surveys/edit-invitation-email)
       :on-click   #(modals/open-modal! survey-template-email-editor-modal/modal-key
                                        {:entity-id  (:db/id template)
                                         :on-submit! (fn [data close-modal!]
                                                       (rf/dispatch [::events/save-email-localizations data close-modal!]))})}
      [:i.fas.fa-envelope]]
     [button/square-secondary-open
      {:tooltip    (loc/tr :settings.surveys/edit-thank-you-page)
       :aria-label (loc/tr :settings.surveys/edit-thank-you-page)
       :on-click   #(modals/open-modal! survey-template-thank-you-editor-modal/modal-key
                                        {:entity-id  (:db/id template)
                                         :on-submit! (fn [data close-modal!]
                                                       (rf/dispatch [::events/save-thank-you-localizations data close-modal!]))})}
      [:i.fas.fa-smile]]
     [button/square-secondary-open
      {:tooltip    (loc/tr :settings.surveys/clone-template)
       :aria-label (loc/tr :settings.surveys/clone-template)
       :on-click   #(rf/dispatch [::events/clone-templates [(:db/id template)]])}
      [:i.fas.fa-clone]]]]])

(defn rotation-period-detail-row
  [{:keys [rotation-period survey-type-id]}]
  [:tr
   {:key   (str "rotation-period-detail-" (:db/id rotation-period))
    :class "*:px-3 *:py-2 *:text-sm"}
   [:td
    {:class "w-full"}
     [:div {:class "font-medium"} (:rotation-period/rotation-period-label rotation-period)]
     [:div {:class "text-xs text-base-light-text-lighter"}
     ;; DEV-8200 removed the rotation period key from this line: cycles now
     ;; reference the rotation period directly, so the key is neither written
     ;; nor meaningful.
     (str (localized-option-label rotation-period-options/rotation-type-options
                                  (:rotation-period/rotation-type rotation-period))
          " | "
          (localized-option-label rotation-period-options/rotation-period-status-options
                                  (:rotation-period/rotation-period-status rotation-period)))]]
   [:td
    {:class "whitespace-nowrap"}
    [:div {:class "flex items-center gap-2 justify-end"}
     [button/square-secondary-open
      {:tooltip    (loc/tr :settings.surveys/edit-rotation-period)
       :aria-label (loc/tr :settings.surveys/edit-rotation-period)
       :on-click   #(open-entity-editor! {:entity-id            (:db/id rotation-period)
                                          :containing-entity-id survey-type-id})}
      [:i.fas.fa-pen]]
     (when (session/is-admin?)
       (let [archived? (rotation-period-options/archived? rotation-period)
             label (if archived?
                     (loc/tr :settings.surveys/unarchive-rotation-period)
                     (loc/tr :settings.surveys/archive-rotation-period))]
         [button/square-secondary-open
          {:tooltip    label
           :aria-label label
           :on-click   (fn []
                         (rf/dispatch [::events/set-rotation-period-status
                                       (:db/id rotation-period)
                                       (if archived?
                                         :rotation-period.status/active
                                         :rotation-period.status/archived)]))}
          (if archived?
            [:i.fas.fa-undo]
            [:i.fas.fa-archive])]))
     (when (session/is-admin?)
       [button/square-secondary-open
        {:tooltip    (loc/tr :settings.surveys/delete-rotation-period)
         :aria-label (loc/tr :settings.surveys/delete-rotation-period)
         :on-click   (fn []
                       (modals/open-modal!
                        delete-confirmation-check-usage/modal-key
                        {:body           (loc/tr :delete-confirmation-body-text
                                                 [(loc/tr :entity.type/rotation-period)
                                                  (:rotation-period/rotation-period-label rotation-period)])
                         :body-caution   (loc/tr :settings.surveys/delete-rotation-period-caution)
                         :conflicts-text (loc/tr :settings.surveys/delete-rotation-period-used-in-cycles)
                         :entity-id      (:db/id rotation-period)}))}
        [:i.fas.fa-trash-alt]])]]])

(defn reminder-rule-detail-row
  [{:keys [reminder-rule survey-type-id]}]
  [:tr
   {:key   (str "reminder-rule-detail-" (:db/id reminder-rule))
    :class "*:px-3 *:py-2 *:text-sm"}
   [:td
    {:class "w-full"}
     [:div {:class "font-medium"} (:reminder-rule/rule-label reminder-rule)]
     [:div {:class "text-xs text-base-light-text-lighter"}
     (str (reminder-rule-options/schedule-text
           {:reminder-anchor  (:reminder-rule/reminder-anchor reminder-rule)
            :offset-type      (:reminder-rule/offset-type reminder-rule)
            :offset-value     (:reminder-rule/offset-value reminder-rule)
            :offset-direction (:reminder-rule/offset-direction reminder-rule)})
          " | "
          (localized-option-label reminder-rule-options/reminder-rule-status-options
                                  (:reminder-rule/reminder-rule-status reminder-rule)))]]
   [:td
    {:class "whitespace-nowrap"}
    [:div {:class "flex items-center gap-2 justify-end"}
     [button/square-secondary-open
      {:tooltip    (loc/tr :settings.surveys/edit-reminder-rule)
       :aria-label (loc/tr :settings.surveys/edit-reminder-rule)
       :on-click   #(open-entity-editor! {:entity-id            (:db/id reminder-rule)
                                          :containing-entity-id survey-type-id})}
      [:i.fas.fa-pen]]]]])

(defn survey-cycle-detail-row
  [{:keys [survey-cycle survey-type-id]}]
  [:tr
   {:key   (str "survey-cycle-detail-" (:db/id survey-cycle))
    :class "*:px-3 *:py-2 *:text-sm"}
   [:td
    {:class "w-full"}
     [:div {:class "font-medium"} (:survey-cycle/cycle-label survey-cycle)]
     [:div {:class "text-xs text-base-light-text-lighter"}
     (str (localized-option-label survey-cycle-options/cycle-status-options
                                  (:survey-cycle/cycle-status survey-cycle))
          (when-let [period-end (:survey-cycle/timestamp-period-end survey-cycle)]
            (str " | " (format-cycle-date period-end))))]]
   [:td
    {:class "whitespace-nowrap"}
    [:div {:class "flex items-center gap-2 justify-end"}
     (when (mass-sending/enabled?)
       [button/square-secondary-open
        {:tooltip    (loc/tr :settings.surveys/open-cycle-actions)
         :aria-label (loc/tr :settings.surveys/open-cycle-actions)
         :on-click   #(modals/open-modal! survey-cycle-actions-modal/modal-key
                                          {:survey-cycle-id (:db/id survey-cycle)})}
        [:i.fas.fa-play]])
     [button/square-secondary-open
      {:tooltip    (loc/tr :settings.surveys/edit-survey-cycle)
       :aria-label (loc/tr :settings.surveys/edit-survey-cycle)
       :on-click   #(open-entity-editor! {:entity-id            (:db/id survey-cycle)
                                          :containing-entity-id survey-type-id})}
      [:i.fas.fa-pen]]
     (when (session/is-admin?)
       [button/square-secondary-open
        {:tooltip    (loc/tr :settings.surveys/delete-survey-cycle)
         :aria-label (loc/tr :settings.surveys/delete-survey-cycle)
         :on-click   (fn []
                       (modals/open-modal!
                        delete-confirmation-check-usage/modal-key
                        {:body         (loc/tr :delete-confirmation-body-text
                                               [(loc/tr :entity.type/survey-cycle)
                                                (:survey-cycle/cycle-label survey-cycle)])
                         :body-caution (loc/tr :settings.surveys/delete-survey-cycle-caution)
                         :entity-id    (:db/id survey-cycle)}))}
        [:i.fas.fa-trash-alt]])]]])

(defn detail-section
  [{:keys [title entity-type containing-entity-id entities row-fn empty-text]}]
  [:div
   {:class "flex flex-col gap-2"}
   [detail-section-title title entity-type containing-entity-id]
   [detail-table
    (if (seq entities)
      (mapv row-fn entities)
      [[:tr
        {:key   (str (name entity-type) "-empty")
         :class "*:px-3 *:py-3 *:text-sm"}
        [:td
         {:class "text-base-light-text-lighter"}
         empty-text]]])]])

(def ^:private detail-entity-scope-instance-id
  :survey-type-detail)

(defn resolve-children
  "Resolve a survey type's child ids into entities, dropping the ones that are
   not (yet) in the session.

   Rotation periods, reminder rules and survey cycles are not bulk-synced - see
   ai/sync-model.md - so a child id only resolves once this view's own
   entity-scope subscription has delivered it. An unresolved id used to be
   rendered anyway, which produced a row whose label and every detail were nil:
   the three sections showed up as the bare separators `|`, `| |` and an empty
   line, and each row's edit button opened the entity editor on a nil `:db/id`."
  [ids sort-key-fn]
  (->> ids
       (map resolve-entity)
       (filter :db/id)
       (sort-by sort-key-fn)))

(defn- section-empty-text
  "While the subscription is still in flight there is a window where the survey
   type names children that have not arrived yet. Saying \"no rotation periods
   yet\" there would be a lie, so distinguish empty from not-loaded-yet."
  [{:keys [loading? child-ids resolved empty-text]}]
  (if (and loading?
           (seq child-ids)
           (empty? resolved))
    (loc/tr :settings.surveys/loading-details)
    empty-text))

(defn survey-type-detail-view
  "Expanded detail panel for one survey type.

   Owns an entity-scope subscription rooted at the survey type, because three of
   its four sections render entity types the new-grid bulk sync does not deliver
   (ai/sync-model.md says to give the view its own subscription rather than
   widen the generic sync set, and all three sit one ref-hop from the survey
   type). Keeping it open rather than fetching once also means children created
   from this panel's own + buttons appear without a reload."
  [_survey-type-id]
  (let [loading?      (r/atom true)
        subscribed-id (r/atom nil)
        close-scope!  (fn []
                        (when-let [id @subscribed-id]
                          (entity-scope/close! id detail-entity-scope-instance-id)
                          (reset! subscribed-id nil)))
        sync-scope!   (fn [id]
                        ;; ag-grid reuses detail cell renderers across rows, so
                        ;; the id can change under a mounted component.
                        (when-not (= id @subscribed-id)
                          (close-scope!)
                          (when (int? id)
                            (reset! subscribed-id id)
                            (reset! loading? true)
                            (entity-scope/open-merging!
                             {:entity-id   id
                              :instance-id detail-entity-scope-instance-id
                              :on-entities (fn [_entities]
                                             (reset! loading? false))
                              :on-error    (fn [error]
                                             (reset! loading? false)
                                             (logging/error
                                              "Survey type detail entity scope failed : "
                                              id " >> " error))}))))]
    (r/create-class
     {:component-did-mount
      (fn [this]
        (sync-scope! (second (r/argv this))))

      :component-did-update
      (fn [this _old-argv]
        (sync-scope! (second (r/argv this))))

      :component-will-unmount
      (fn [_this]
        (close-scope!))

      :reagent-render
      (fn [survey-type-id]
        (let [survey-type (when survey-type-id
                            (session/get-entity survey-type-id))
              survey-templates (->> @session/survey-templates
                                    (remove :common/disabled?)
                                    (filter (fn [template]
                                              (= survey-type-id
                                                 (:survey-template/survey-type template)))))
              rotation-period-ids (:survey-type/rotation-periods survey-type)
              reminder-rule-ids (:survey-type/reminder-rules survey-type)
              survey-cycle-ids (:survey-type/survey-cycles survey-type)
              rotation-periods (->> (resolve-children
                                     rotation-period-ids
                                     (juxt :common/ord :db/id))
                                    (remove :common/disabled?))
              reminder-rules (resolve-children
                              reminder-rule-ids
                              (juxt :common/ord :db/id))
              survey-cycles (->> (resolve-children
                                  survey-cycle-ids
                                  (juxt :survey-cycle/timestamp-period-end
                                        :survey-cycle/cycle-label))
                                 (remove :common/disabled?))]
          [:div
           {:class "p-4 bg-base-lightest"}
           [:div
            {:class "max-w-5xl flex flex-col gap-4"}
            [detail-section {:title                (loc/tr :settings.surveys/templates-heading)
                             :entity-type          :entity.type/survey-template
                             :containing-entity-id survey-type-id
                             :entities             survey-templates
                             :row-fn               (fn [template]
                                                    (template-detail-row
                                                     {:template       template
                                                      :survey-type-id survey-type-id}))
                             :empty-text           (loc/tr :settings.surveys/no-templates)}]
            [detail-section {:title                (loc/tr :settings.surveys/rotation-periods-heading)
                             :entity-type          :entity.type/rotation-period
                             :containing-entity-id survey-type-id
                             :entities             rotation-periods
                             :row-fn               (fn [rotation-period]
                                                    (rotation-period-detail-row
                                                     {:rotation-period rotation-period
                                                      :survey-type-id  survey-type-id}))
                             :empty-text           (section-empty-text
                                                    {:loading?   @loading?
                                                     :child-ids  rotation-period-ids
                                                     :resolved   rotation-periods
                                                     :empty-text (loc/tr :settings.surveys/no-rotation-periods)})}]
            [detail-section {:title                (loc/tr :settings.surveys/reminder-rules-heading)
                             :entity-type          :entity.type/reminder-rule
                             :containing-entity-id survey-type-id
                             :entities             reminder-rules
                             :row-fn               (fn [reminder-rule]
                                                    (reminder-rule-detail-row
                                                     {:reminder-rule  reminder-rule
                                                      :survey-type-id survey-type-id}))
                             :empty-text           (section-empty-text
                                                    {:loading?   @loading?
                                                     :child-ids  reminder-rule-ids
                                                     :resolved   reminder-rules
                                                     :empty-text (loc/tr :settings.surveys/no-reminder-rules)})}]
            [detail-section {:title                (loc/tr :settings.surveys/survey-cycles-heading)
                             :entity-type          :entity.type/survey-cycle
                             :containing-entity-id survey-type-id
                             :entities             survey-cycles
                             :row-fn               (fn [survey-cycle]
                                                    (survey-cycle-detail-row
                                                     {:survey-cycle   survey-cycle
                                                      :survey-type-id survey-type-id}))
                             :empty-text           (section-empty-text
                                                    {:loading?   @loading?
                                                     :child-ids  survey-cycle-ids
                                                     :resolved   survey-cycles
                                                     :empty-text (loc/tr :settings.surveys/no-survey-cycles)})}]]]))})))

(def survey-type-columns
  (concat
   [;; Pinned so the row stays identifiable while the grid is scrolled
    ;; horizontally - ag-grid virtualizes an off-screen column out of the DOM,
    ;; so an unpinned name column does not simply sit out of sight, it is gone.
    {:headerName   (loc/tr :survey-type-label)
     :pinned       :left
     :sortable     true
     :sort         "asc"
     :field        :survey-type/label
     :cellRenderer "agGroupCellRenderer"}
    {:headerName   (loc/tr :survey-type/entity-types)
     :sortable     true
     :field        :survey-type/entity-types
     :cellRenderer (fn [props]
                     (table-helpers/multi-kw-renderer (table/get-cell-value props)))}
    {:headerName   (loc/tr :survey-type/teams-access)
     :sortable     true
     :field        :survey-type/teams-access
     :cellRenderer (fn [props]
                     (table-helpers/multi-ref-renderer (table/get-cell-value props)))}
    {:headerName   (loc/tr :survey-type-template-count)
     :sortable     true
     :field        :survey-type-template-count
     :cellRenderer (fn [props]
                     (let [value (table/get-cell-value props)]
                       value))}
    {:headerName   "Rotation periods"
     :sortable     true
     :field        :survey-type-rotation-period-count
     :cellRenderer table/get-cell-value}
    {:headerName   "Reminder rules"
     :sortable     true
     :field        :survey-type-reminder-rule-count
     :cellRenderer table/get-cell-value}
    {:headerName   "Survey cycles"
     :sortable     true
     :field        :survey-type-cycle-count
     :cellRenderer table/get-cell-value}
    ;; Pinned right and sized to its contents. It used to be 520px wide with an
    ;; empty header, which pushed the buttons away from the counts they act on
    ;; and put them past the right edge at laptop width - and reaching them
    ;; meant scrolling the survey type's name out of the DOM, so you clicked
    ;; create on a row you could no longer identify.
    {:colId        "actions"
     :headerName   (loc/tr :settings.surveys/actions-column)
     :pinned       :right
     :resizable    false
     :sortable     false
     :suppressHeaderMenuButton true
     :suppressHeaderContextMenu true
     :width        180
     :cellRenderer (fn [props]
                     (let [survey-type-id (table-helpers/get-row-data-id props)]
                       (r/as-element
                        [:div
                         {:class "h-full flex items-center gap-2"}
                         (when survey-type-id
                           ;; Each button says what it creates, in the tooltip
                           ;; and again as an accessible name - the icon is
                           ;; otherwise the only thing telling them apart, and a
                           ;; screen reader reads four anonymous buttons.
                           (for [{:keys [entity-type icon label]}
                                 [{:entity-type :entity.type/rotation-period
                                   :icon        :fa-repeat
                                   :label       (loc/tr :settings.surveys/create-rotation-period)}
                                  {:entity-type :entity.type/reminder-rule
                                   :icon        :fa-bell
                                   :label       (loc/tr :settings.surveys/create-reminder-rule)}
                                  {:entity-type :entity.type/survey-cycle
                                   :icon        :fa-calendar
                                   :label       (loc/tr :settings.surveys/create-survey-cycle)}]]
                             ^{:key (str "create-" entity-type)}
                             [button/square-secondary-open
                              {:tooltip    label
                               :aria-label label
                               :on-click   #(create-child-entity! entity-type survey-type-id)}
                              [:i {:class (str "fas " (name icon))}]]))
                         (when survey-type-id
                           [button/square-secondary-open
                            {:tooltip    (loc/tr :settings.surveys/edit-survey-type)
                             :aria-label (loc/tr :settings.surveys/edit-survey-type)
                             :on-click   #(modals/open-modal! entity-editor/modal-key
                                                              {:entity-id survey-type-id})}
                            [:i.fas.fa-pen]])])))}]))

(defn resolve-survey-type-data
  [survey-templates-by-type survey-type]
  (-> survey-type
      (update :survey-type/entity-types
              (fn [entity-types]
                (mapv table-helpers/kw->js entity-types)))
      (assoc :survey-type-template-count
             (count (get survey-templates-by-type (:db/id survey-type))))
      (assoc :survey-type-rotation-period-count
             (count (:survey-type/rotation-periods survey-type)))
      (assoc :survey-type-reminder-rule-count
             (count (:survey-type/reminder-rules survey-type)))
      (assoc :survey-type-cycle-count
             (count (:survey-type/survey-cycles survey-type)))))

(defn survey-types-table
  [state _survey-types _survey-templates-by-type]
  (r/create-class
    {:component-did-mount
     (fn [_])
     :component-did-update
     (fn [_args]
       (let [grid-ref (get-in @state [:grid :ref])
             grid-api (aget grid-ref "api")
             for-each-node-fn (aget grid-api "forEachNode")
             set-row-node-expanded-fn (aget grid-api "setRowNodeExpanded")]
         (.call for-each-node-fn
                grid-api
                (fn [node]
                   (let [data (aget node "data")
                         id (aget data "id")]
                     (when (some #{id} (:expanded-survey-types @state))
                       (.call set-row-node-expanded-fn grid-api node true)))))))
     :reagent-render
     (fn [state survey-types survey-templates-by-type]
       (let [row-data (mapv (partial resolve-survey-type-data survey-templates-by-type) survey-types)
             table-props
             {:grid-id               "survey-types-table"
              :state-atom            state
              ;; Not embedded: an embedded full-width row is rendered once per
              ;; column container, so with the name pinned left and the actions
              ;; pinned right the detail panel appeared three times, each
              ;; clipped to its container. Unembedded, it spans the grid and
              ;; floats over the pinned areas, which is what a detail panel
              ;; wants anyway.
              :master-detail-options {:embedFullWidthRows       false
                                      :isRowMaster              (fn [data]
                                                                  (boolean (-> data
                                                                               (js->clj :keywordize-keys true)
                                                                               :id)))
                                      :detailCellRenderer       (fn [params]
                                                                  (let [row-data (-> params
                                                                                     (aget "data")
                                                                                     (js->clj :keywordize-keys true))]
                                                                    (r/as-element
                                                                     [survey-type-detail-view (or (:db/id row-data)
                                                                                                  (:id row-data))])))}
              :enable-cell-text-selection false
             :column-defs                 survey-type-columns
             :row-data                    row-data
             :get-row-id                  (fn [params]
                                            (some-> (or (some-> params
                                                                (aget "data")
                                                                (aget "id"))
                                                        (some-> params
                                                                (aget "data")
                                                                (aget "db/id")))
                                                    str))
             :default-col-def             {:sortable  true
                                            :resizable true
                                            :editable  false
                                            :menuTabs  ["generalMenuTab"]}
             :row-selection              {:mode "singleRow"
                                          :checkboxes false}
              :min-height                  "300px"
              :auto-height?                true
              :width                       "100%"
              :on-selection-changed        #(rf/dispatch [::table-events/single-select-changed [::sub/selected-survey-type] %])
              :on-row-group-opened         (fn [row-event]
                                             (let [{:keys [expanded data]} (js->clj row-event :keywordize-keys true)]
                                               (swap! state update
                                                      :expanded-survey-types
                                                      (fn [existing]
                                                        (if expanded
                                                          (set/union (set existing) #{(:id data)})
                                                          (set/difference (set existing) #{(:id data)}))))))}]
         [:div
          {:class "py-6 h-full flex flex-col gap-4 min-h-0"}
          [:p
           (loc/tr :settings.survey-types/top-text [(string/lower-case (loc/tr :module/survey))])]
          [:div
           {:class "flex-1 min-h-0"}
           [table/view (assoc table-props
                              :auto-height? false
                              :min-height "100%"
                              :max-height "100%")]]]))}))

(defn view
  [state survey-types survey-templates-by-type]
  [survey-types-table state survey-types survey-templates-by-type])
